import { defineStore } from 'pinia';
import { ref } from 'vue';
import * as authApi from '@/api/auth';
import type { AdminUserInfo, MenuItem } from '@/types/api';
import { storage, STORAGE_KEYS } from '@/utils/storage';

export const useUserStore = defineStore('user', () => {
  const token      = ref<string>(storage.get<string>(STORAGE_KEYS.TOKEN) || '');
  const user       = ref<AdminUserInfo | null>(storage.get<AdminUserInfo>(STORAGE_KEYS.USER));
  const menus      = ref<MenuItem[]>(storage.get<MenuItem[]>(STORAGE_KEYS.MENUS) || []);
  const rules      = ref<string[]>(storage.get<string[]>(STORAGE_KEYS.RULES) || []);
  const loaded     = ref(false);

  function setToken(t: string) { token.value = t; storage.set(STORAGE_KEYS.TOKEN, t); }
  function setUser(u: AdminUserInfo) { user.value = u; storage.set(STORAGE_KEYS.USER, u); }
  function setMenus(m: MenuItem[])   { menus.value = m; storage.set(STORAGE_KEYS.MENUS, m); }
  function setRules(r: string[])     { rules.value = r; storage.set(STORAGE_KEYS.RULES, r); }

  async function passwordLogin(username: string, password: string) {
    const r = await authApi.pasLogin(username, password);
    setToken(r.token);
    setUser(r.info);
    return r;
  }
  async function codeLogin(mobile: string, code: string) {
    const r = await authApi.codeLogin(mobile, code);
    setToken(r.token);
    setUser(r.info);
    return r;
  }

  async function fetchProfile(force = false) {
    if (loaded.value && !force) return;
    const r = await authApi.me();
    setUser(r.user);
    setMenus(r.menus);
    setRules(r.ruleNames);
    loaded.value = true;
    return r;
  }

  async function logout(silent = false) {
    if (!silent) {
      try { await authApi.logout(); } catch {}
    }
    token.value = '';
    user.value = null;
    menus.value = [];
    rules.value = [];
    loaded.value = false;
    storage.clear();
  }

  function hasPermission(code: string): boolean {
    if (user.value?.is_super) return true;
    return rules.value.includes(code);
  }

  return {
    token, user, menus, rules, loaded,
    setToken, setUser, setMenus, setRules,
    passwordLogin, codeLogin, fetchProfile, logout, hasPermission,
  };
});
